import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { extraBoxLine, majorityFlavor, syncExtraUnit } from '@theme/extra-box';
import {
  bonusLineProperties,
  linkBonusBundle,
  perBoxCentsFromTotal,
  subscriptionCartBody,
  syncFreeBoxOffer,
} from '@theme/free-box-offer';
import { fetchConfig } from '@theme/utilities';

const SKIO_BOX_ENDPOINT = 'https://api.skio.com/storefront-http/get-classic-box-v2';

const DEFAULT_CURRENCY_DECIMALS = 2;

/** Currencies that don't use 2 decimal places */
const CURRENCY_DECIMALS = {
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  UYI: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3,
};

/**
 * @typedef {object} SkioSelectableVariant
 * @property {string} platformId - The variant gid, e.g. gid://shopify/ProductVariant/123
 * @property {number} quantity
 * @property {string} productTitle
 * @property {string} productVariantTitle
 * @property {string} imageSrc
 * @property {number} price
 * @property {boolean} [outOfStock]
 */

/**
 * @typedef {object} SkioBoxConfig
 * @property {string} platformId
 * @property {number} size
 * @property {string} title
 * @property {number} price
 * @property {string} currencyCode
 * @property {SkioSelectableVariant[]} selectableProductVariants
 */

/**
 * A custom element that renders a Skio static box bundle builder.
 *
 * Tiers are not explicitly selected: customers add items and the progress bar
 * advances through the tier markers. The tier whose size exactly matches the
 * selected count is the box variant that gets added to cart.
 *
 * @typedef {object} BoxBuilderRefs
 * @property {HTMLElement[]} tierMarker - Tier markers on the progress bar (one per box variant).
 * @property {HTMLElement} progressFill - The progress bar fill element.
 * @property {HTMLElement} flavorSkeleton - Skeleton shown while the Skio box config loads.
 * @property {HTMLElement} flavorList - Container the flavor rows render into.
 * @property {HTMLElement} flavorError - Error panel shown when the Skio fetch fails.
 * @property {HTMLElement} flavorErrorMessage - Error message element.
 * @property {HTMLTemplateElement} flavorRowTemplate - Template for a single flavor row.
 * @property {HTMLElement} progressLabel - "X of N selected" label.
 * @property {HTMLElement} [scrollHint] - Scroll hint shown when the flavor list overflows.
 * @property {HTMLButtonElement} atcButton - Add to cart button.
 * @property {HTMLElement} atcLabel - Add to cart button label.
 * @property {HTMLElement} atcError - Add to cart error message.
 * @property {HTMLElement} liveRegion - Screen reader live region.
 * @property {HTMLTemplateElement} moneyFormat - The shop money format template.
 * @property {HTMLButtonElement} [oneTimeButton] - Quiet one-time add button.
 * @property {HTMLElement} [oneTimeLinkLabel] - Quiet one-time add button label.
 * @property {HTMLElement} [purchaseOptions] - Subscribe versus one-time choice.
 * @property {HTMLInputElement} [subscribeRadio]
 * @property {HTMLInputElement} [oneTimeRadio]
 * @property {HTMLElement} [subscribePrice]
 * @property {HTMLElement} [subscribeCompare]
 * @property {HTMLElement} [oneTimePlanPrice]
 * @property {HTMLElement} [saveBadge]
 * @property {HTMLElement} [trialNote]
 * @property {HTMLElement} [flavorHead]
 * @property {HTMLElement} [addedBadge]
 *
 * @extends {Component<BoxBuilderRefs>}
 */
class BoxBuilderComponent extends Component {
  requiredRefs = [
    'tierMarker',
    'progressFill',
    'flavorSkeleton',
    'flavorList',
    'flavorError',
    'flavorErrorMessage',
    'flavorRowTemplate',
    'progressLabel',
    'atcButton',
    'atcLabel',
    'liveRegion',
    'moneyFormat',
  ];

  /** @type {'subscribe' | 'one-time'} */
  #purchaseChoice = 'subscribe';

  /** @type {string} */
  #extraGid = '';
  /** @type {MutationObserver | null} */
  #tierPriceObserver = null;

  /** @type {boolean} */
  #extraTouched = false;

  /** @type {Map<string, Promise<SkioBoxConfig>>} */
  #boxCache = new Map();

  /** @type {Map<string, number>} variant gid -> selected quantity */
  #selections = new Map();

  /** @type {Map<string, SkioSelectableVariant>} variant gid -> Skio flavor data */
  #flavorMeta = new Map();

  /**
   * Lowercased product titles and/or numeric variant ids that should show the
   * flavor badge, from the "Badged flavors" section setting.
   *
   * @type {Set<string>}
   */
  get #badgedFlavors() {
    this.#badgedFlavorsCache ??= new Set(
      (this.dataset.flavorBadgeItems ?? '')
        .split(',')
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean)
    );

    return this.#badgedFlavorsCache;
  }

  /** @type {Set<string> | null} */
  #badgedFlavorsCache = null;

  /**
   * Skio only returns a variant gid and a product title, so match on either.
   *
   * @param {SkioSelectableVariant} flavor
   * @returns {boolean}
   */
  #isBadgedFlavor(flavor) {
    const badged = this.#badgedFlavors;
    const numericId = flavor.platformId?.split('/').pop()?.toLowerCase() ?? '';

    return badged.has(flavor.productTitle?.trim().toLowerCase() ?? '') || badged.has(numericId);
  }

  /** @type {'idle' | 'loading' | 'ready' | 'error' | 'adding'} */
  #status = 'idle';

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | undefined} */
  #errorTimeout;

  /** @type {Map<HTMLDetailsElement, Animation>} */
  #accordionAnimations = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    this.#loadBoxes();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    for (const animation of this.#accordionAnimations.values()) animation.cancel();
    this.#accordionAnimations.clear();
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
  }

  /** @returns {HTMLElement[]} Tier markers sorted by box size, ascending */
  get #markers() {
    return (this.refs.tierMarker ?? [])
      .slice()
      .sort((a, b) => Number(a.dataset.size) - Number(b.dataset.size));
  }

  /** @returns {number} The largest tier size — the hard cap on selections */
  get #maxSize() {
    const markers = this.#markers;
    return Number(markers[markers.length - 1]?.dataset.size) || 1;
  }

  /** @returns {HTMLElement | undefined} The tier whose size exactly matches the selected count */
  get #activeTier() {
    const total = this.#totalSelected;
    return this.#markers.find((marker) => Number(marker.dataset.size) === total);
  }

  /** @returns {HTMLElement} The tier the customer is currently building toward */
  get #targetTier() {
    const total = Math.max(this.#totalSelected, 1);
    const markers = this.#markers;
    return markers.find((marker) => Number(marker.dataset.size) >= total) ?? markers[markers.length - 1];
  }

  /** @returns {string} The subscription selling plan id, or '' when unavailable */
  get #subscriptionSellingPlanId() {
    return this.dataset.sellingPlanId ?? '';
  }

  get #totalSelected() {
    let total = 0;
    for (const qty of this.#selections.values()) total += qty;
    return total;
  }

  /**
   * Increments the quantity for the flavor row the event originated from.
   *
   * @param {Event} event
   */
  increment(event) {
    const gid = this.#rowGid(event);
    if (!gid) return;
    if (this.#totalSelected >= this.#maxSize) return;
    if (this.#flavorMeta.get(gid)?.outOfStock) return;

    this.#selections.set(gid, (this.#selections.get(gid) ?? 0) + 1);
    this.#updateSelectionUI();
  }

  /**
   * Decrements the quantity for the flavor row the event originated from.
   *
   * @param {Event} event
   */
  decrement(event) {
    const gid = this.#rowGid(event);
    if (!gid) return;

    const qty = (this.#selections.get(gid) ?? 0) - 1;
    if (qty <= 0) {
      this.#selections.delete(gid);
    } else {
      this.#selections.set(gid, qty);
    }

    this.#updateSelectionUI();
  }

  /**
   * Retries loading the Skio box configs.
   */
  retry() {
    this.#boxCache.clear();
    this.#loadBoxes();
  }

  /**
   * Smoothly opens and closes the native details accordions.
   *
   * @param {Event} event
   */
  toggleAccordion(event) {
    if (!(event.target instanceof Element)) return;

    const summary = event.target.closest('.ctr-box-builder__accordion summary');
    if (!(summary instanceof HTMLElement) || !this.contains(summary)) return;

    const accordion = summary.closest('.ctr-box-builder__accordion');
    const content = accordion?.querySelector('.ctr-box-builder__accordion-content');
    if (!(accordion instanceof HTMLDetailsElement) || !(content instanceof HTMLElement)) return;

    event.preventDefault();
    this.#toggleAccordion(accordion, content);
  }

  /**
   * @param {HTMLDetailsElement} accordion
   * @param {HTMLElement} content
   */
  #toggleAccordion(accordion, content) {
    const existingAnimation = this.#accordionAnimations.get(accordion);
    if (existingAnimation) {
      existingAnimation.cancel();
      this.#accordionAnimations.delete(accordion);
      accordion.classList.remove('ctr-box-builder__accordion--closing');
      this.#resetAccordionContent(content);
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      accordion.open = !accordion.open;
      return;
    }

    if (accordion.open) {
      this.#animateAccordionClose(accordion, content);
    } else {
      this.#animateAccordionOpen(accordion, content);
    }
  }

  /**
   * @param {HTMLDetailsElement} accordion
   * @param {HTMLElement} content
   */
  #animateAccordionOpen(accordion, content) {
    accordion.classList.remove('ctr-box-builder__accordion--closing');
    accordion.open = true;
    const targetHeight = content.scrollHeight;

    content.style.height = '0px';
    content.style.opacity = '0';
    content.style.overflow = 'hidden';

    const animation = content.animate(
      [
        { height: '0px', opacity: 0 },
        { height: `${targetHeight}px`, opacity: 1 },
      ],
      { duration: 260, easing: 'cubic-bezier(0.2, 0.65, 0.2, 1)' }
    );

    this.#accordionAnimations.set(accordion, animation);
    animation.onfinish = () => {
      this.#accordionAnimations.delete(accordion);
      this.#resetAccordionContent(content);
    };
  }

  /**
   * @param {HTMLDetailsElement} accordion
   * @param {HTMLElement} content
   */
  #animateAccordionClose(accordion, content) {
    const startHeight = content.scrollHeight;

    accordion.classList.add('ctr-box-builder__accordion--closing');
    content.style.height = `${startHeight}px`;
    content.style.opacity = '1';
    content.style.overflow = 'hidden';

    const animation = content.animate(
      [
        { height: `${startHeight}px`, opacity: 1 },
        { height: '0px', opacity: 0 },
      ],
      { duration: 220, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
    );

    this.#accordionAnimations.set(accordion, animation);
    animation.onfinish = () => {
      accordion.open = false;
      accordion.classList.remove('ctr-box-builder__accordion--closing');
      this.#accordionAnimations.delete(accordion);
      this.#resetAccordionContent(content);
    };
  }

  /**
   * @param {HTMLElement} content
   */
  #resetAccordionContent(content) {
    content.style.height = '';
    content.style.opacity = '';
    content.style.overflow = '';
  }

  /**
   * Adds the configured box to the cart as a subscription.
   */
  async addToCart() {
    const sellingPlanId = this.dataset.purchaseMode === 'one-time' ? '' : this.#subscriptionSellingPlanId;
    await this.#addConfiguredBox(sellingPlanId, this.refs.atcButton);
  }

  /**
   * Adds the configured box to the cart without a selling plan.
   */
  async addOneTimeToCart() {
    await this.#addConfiguredBox('', this.refs.oneTimeButton);
  }

  /**
   * Switches a completed box between the selling plan and a one-time purchase.
   * @param {Event} event
   */
  selectPurchase(event) {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    if (!input) return;
    this.#purchaseChoice = input.value === 'one-time' ? 'one-time' : 'subscribe';
    this.#updateFooter();
  }

  /**
   * Adds the configured box to the cart with the Skio static box line item properties.
   *
   * @param {string} sellingPlanId
   * @param {HTMLButtonElement | undefined} triggerButton
   */
  async #addConfiguredBox(sellingPlanId, triggerButton) {
    const tier = this.#activeTier;
    if (!tier || this.#status !== 'ready') return;

    const { atcButton, atcError, oneTimeButton } = this.refs;

    /** @type {Record<string, string | number | boolean>} */
    const properties = { _isClassicBoxV2: true };

    for (const [gid, qty] of this.#selections) {
      const meta = this.#flavorMeta.get(gid);
      if (!qty || !meta) continue;

      const hasVariantTitle = meta.productVariantTitle && meta.productVariantTitle !== 'Default Title';
      const humanKey = hasVariantTitle ? `${meta.productTitle} - ${meta.productVariantTitle}` : meta.productTitle;

      properties[humanKey] = qty;
      properties[`_pv${gid}`] = qty;
    }

    const scheduleOffer = this.dataset.freeBoxOffer === 'true';
    if (sellingPlanId && scheduleOffer) Object.assign(properties, bonusLineProperties(this));

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : null))
      .filter(Boolean);

    const line = {
      id: Number(tier.dataset.variantId),
      quantity: 1,
      ...(sellingPlanId && { selling_plan: Number(sellingPlanId) }),
      properties,
    };
    const payload = subscriptionCartBody(
      line,
      this,
      Boolean(sellingPlanId) && scheduleOffer,
      sectionIds.join(','),
      window.location.pathname
    );
    if (this.dataset.extraBoxOffer === 'true' && !scheduleOffer && Number(tier.dataset.size) === 8) {
      const extra = extraBoxLine(this.#extraGid);
      if (extra && extra.id !== Number(line.id)) {
        linkBonusBundle(line, extra);
        payload.items.push(extra);
      }
    }

    this.#status = 'adding';
    triggerButton?.setAttribute('aria-busy', 'true');
    atcButton.disabled = true;
    if (oneTimeButton) oneTimeButton.disabled = true;
    atcError.hidden = true;
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify(payload) })
      ).then((res) => res.json());

      if (response.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, response.message, response.description, response.errors));

        atcError.textContent = response.message;
        atcError.hidden = false;
        this.refs.liveRegion.textContent = response.message;

        this.#errorTimeout = setTimeout(() => {
          atcError.hidden = true;
          this.refs.liveRegion.textContent = '';
        }, 10000);

        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, tier.dataset.variantId ?? '', {
          source: 'box-builder-component',
          itemCount: payload.items.length,
          productId: this.dataset.productId,
          sections: response.sections,
        })
      );

      this.refs.liveRegion.textContent = Theme.translations?.added ?? 'Added to cart';
      this.#selections.clear();
      this.#extraGid = '';
      this.#extraTouched = false;
    } catch (error) {
      console.error(error);
      atcError.textContent = this.dataset.errorMessage ?? 'Something went wrong. Please try again.';
      atcError.hidden = false;
      this.#errorTimeout = setTimeout(() => {
        atcError.hidden = true;
      }, 10000);
    } finally {
      this.#status = 'ready';
      triggerButton?.removeAttribute('aria-busy');
      this.#updateSelectionUI();
    }
  }

  /**
   * Loads the Skio box configs for all tiers, renders the flavor list from the
   * first available config, and reconciles each tier's size against the API.
   */
  async #loadBoxes() {
    const markers = this.#markers;
    if (!markers.length) return;

    this.#setStatus('loading');

    const results = await Promise.allSettled(
      markers.map((marker) => this.#loadBox(marker.dataset.variantGid ?? ''))
    );

    // The Skio API is the source of truth for each tier's size
    results.forEach((result, index) => {
      const marker = markers[index];
      if (result.status === 'fulfilled' && result.value.size && marker) {
        marker.dataset.size = String(result.value.size);
        this.#updateTierPerUnit(marker);
      }
    });

    const firstBox = results.find((result) => result.status === 'fulfilled')?.value;

    if (!firstBox) {
      const reason = results.find((result) => result.status === 'rejected')?.reason;
      console.warn('[ctr-box-builder] Failed to load Skio box config', reason);
      this.refs.flavorErrorMessage.textContent =
        this.dataset.errorMessage ?? 'This product is not registered as a Skio static box.';
      this.#setStatus('error');
      this.#updateFooter();
      return;
    }

    this.#renderFlavorRows(firstBox);
    this.#truncateSelections();
    this.#setStatus('ready');
    this.#updateSelectionUI();
    this.#updateScrollHint();
  }

  /**
   * Shows the scroll hint and fade mask only when the flavor list actually overflows.
   */
  #updateScrollHint() {
    const { flavorList, scrollHint } = this.refs;
    const overflowing = flavorList.scrollHeight > flavorList.clientHeight + 1;

    flavorList.classList.toggle('ctr-box-builder__flavors--overflowing', overflowing);
    if (scrollHint instanceof HTMLElement) scrollHint.hidden = !overflowing;
  }

  /**
   * Fetches (and caches) the Skio box config for a box variant.
   *
   * @param {string} variantGid - The box variant gid.
   * @returns {Promise<SkioBoxConfig>}
   */
  #loadBox(variantGid) {
    const cached = this.#boxCache.get(variantGid);
    if (cached) return cached;

    // Skio expects the numeric variant id, not the gid:// form
    const numericId = variantGid.split('/').pop();

    const promise = fetch(SKIO_BOX_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productVariantPlatformId: numericId }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Skio responded with ${response.status}`);
        return response.json();
      })
      .then((json) => {
        const box = json?.ClassicBoxV2 ?? json?.box ?? json;
        if (!box || !Array.isArray(box.selectableProductVariants)) {
          throw new Error('Skio response is missing selectableProductVariants');
        }
        return box;
      });

    // Don't cache failures so Retry can refetch
    promise.catch(() => this.#boxCache.delete(variantGid));
    this.#boxCache.set(variantGid, promise);

    return promise;
  }

  /**
   * Renders the flavor rows for a box config.
   *
   * @param {SkioBoxConfig} box
   */
  #renderFlavorRows(box) {
    const { flavorList, flavorRowTemplate } = this.refs;

    this.#flavorMeta = new Map();
    flavorList.replaceChildren();

    for (const flavor of box.selectableProductVariants) {
      this.#flavorMeta.set(flavor.platformId, flavor);

      const fragment = /** @type {DocumentFragment} */ (flavorRowTemplate.content.cloneNode(true));
      const row = fragment.querySelector('[data-flavor-row]');
      if (!(row instanceof HTMLElement)) continue;

      row.dataset.variantGid = flavor.platformId;

      const image = row.querySelector('[data-flavor-image]');
      if (image instanceof HTMLImageElement) {
        if (flavor.imageSrc) {
          image.src = flavor.imageSrc;
          image.alt = flavor.productTitle;
        } else {
          image.remove();
        }
      }

      const title = row.querySelector('[data-flavor-title]');
      if (title) title.textContent = flavor.productTitle;

      const badge = row.querySelector('[data-flavor-badge]');
      if (badge instanceof HTMLElement) {
        const badgeLabel = this.dataset.flavorBadgeLabel?.trim();
        if (badgeLabel && this.#badgedFlavors.size && this.#isBadgedFlavor(flavor)) {
          badge.textContent = badgeLabel;
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      }

      const subtitle = row.querySelector('[data-flavor-subtitle]');
      if (subtitle) {
        if (flavor.productVariantTitle && flavor.productVariantTitle !== 'Default Title') {
          subtitle.textContent = flavor.productVariantTitle;
        } else {
          subtitle.remove();
        }
      }

      if (flavor.outOfStock) {
        row.classList.add('ctr-box-builder__flavor-row--sold-out');
        const addButton = row.querySelector('[data-flavor-add]');
        if (addButton instanceof HTMLButtonElement) {
          addButton.disabled = true;
          addButton.textContent = this.dataset.soldOutLabel ?? 'Sold out';
        }
      }

      const addButtonToName = row.querySelector('[data-flavor-add]');
      if (addButtonToName instanceof HTMLButtonElement && flavor.productTitle) {
        addButtonToName.setAttribute('aria-label', addButtonToName.textContent.trim() + ' ' + flavor.productTitle);
      }

      flavorList.appendChild(fragment);
    }

    // Drop selections for flavors that no longer exist in the box
    for (const gid of [...this.#selections.keys()]) {
      if (!this.#flavorMeta.has(gid)) this.#selections.delete(gid);
    }
  }

  /**
   * Truncates selections until they fit the largest tier.
   */
  #truncateSelections() {
    let truncated = false;

    while (this.#totalSelected > this.#maxSize) {
      const lastGid = [...this.#selections.keys()].pop();
      if (!lastGid) break;

      const qty = (this.#selections.get(lastGid) ?? 0) - 1;
      if (qty <= 0) {
        this.#selections.delete(lastGid);
      } else {
        this.#selections.set(lastGid, qty);
      }
      truncated = true;
    }

    if (truncated) {
      this.refs.liveRegion.textContent = this.#interpolate(
        this.dataset.truncatedMessage ?? 'Your box was reduced to [count] items.',
        { count: this.#maxSize }
      );
    }
  }

  /**
   * Toggles the skeleton / list / error panels based on status.
   *
   * @param {'idle' | 'loading' | 'ready' | 'error'} status
   */
  #setStatus(status) {
    this.#status = status;

    const { flavorSkeleton, flavorList, flavorError } = this.refs;
    flavorSkeleton.hidden = status !== 'loading';
    flavorList.hidden = status !== 'ready';
    flavorError.hidden = status !== 'error';
  }

  /**
   * Updates every flavor row's quantity UI plus the progress bar and footer.
   */
  #updateSelectionUI() {
    const full = this.#totalSelected >= this.#maxSize;

    for (const row of this.refs.flavorList.querySelectorAll('[data-flavor-row]')) {
      if (!(row instanceof HTMLElement)) continue;

      const gid = row.dataset.variantGid ?? '';
      const qty = this.#selections.get(gid) ?? 0;
      const soldOut = this.#flavorMeta.get(gid)?.outOfStock ?? false;
      const addButton = row.querySelector('[data-flavor-add]');
      const stepper = row.querySelector('[data-flavor-stepper]');
      const qtyLabel = row.querySelector('[data-flavor-qty]');
      const plusButton = row.querySelector('[data-flavor-plus]');

      if (addButton instanceof HTMLElement) addButton.hidden = qty > 0;
      if (addButton instanceof HTMLButtonElement) addButton.disabled = full || soldOut;
      if (stepper instanceof HTMLElement) stepper.hidden = qty === 0;
      if (qtyLabel) qtyLabel.textContent = String(qty);
      if (plusButton instanceof HTMLButtonElement) plusButton.disabled = full;

      row.classList.toggle('ctr-box-builder__flavor-row--selected', qty > 0);
    }

    this.#updateProgressBar();
    this.#updateFooter();
  }

  /**
   * Updates the progress bar fill and tier marker states.
   *
   * The fill interpolates piecewise between marker positions so it always
   * lines up with the markers regardless of tier size spacing.
   */
  #updateProgressBar() {
    const markers = this.#markers;
    const total = this.#totalSelected;

    let width = 0;
    if (total > 0) {
      width = 100;
      let prevSize = 0;
      let prevPos = 0;

      const lastMarker = markers[markers.length - 1];

      for (const marker of markers) {
        const size = Number(marker.dataset.size);
        const isLast = marker === lastMarker;
        const pos = isLast ? 100 : parseFloat(marker.style.getPropertyValue('--marker-pos')) || 0;

        if (total <= size) {
          width = prevPos + ((total - prevSize) / (size - prevSize)) * (pos - prevPos);
          break;
        }

        prevSize = size;
        prevPos = pos;
      }
    }

    this.refs.progressFill.style.width = `${Math.min(100, Math.max(0, width))}%`;

    for (const marker of markers) {
      const size = Number(marker.dataset.size);
      marker.classList.toggle('ctr-box-builder__progress-marker--reached', total >= size);
      marker.classList.toggle('ctr-box-builder__progress-marker--active', total === size);
    }
  }

  /**
   * Updates the progress label and add to cart button state.
   */
  #updateFooter() {
    const { progressLabel, atcButton, atcLabel, oneTimeButton, oneTimeLinkLabel } = this.refs;
    const total = this.#totalSelected;
    const active = this.#activeTier;
    const target = this.#targetTier;
    const targetSize = Number(target?.dataset.size) || 0;
    const complete = this.#status === 'ready' && Boolean(active);

    progressLabel.textContent = this.#interpolate(this.dataset.progressLabel ?? '[selected] of [count] selected', {
      selected: total,
      count: targetSize,
    });

    atcButton.disabled = !complete || this.#status === 'adding';
    if (oneTimeButton) oneTimeButton.disabled = !complete || this.#status === 'adding';

    if (complete && active) {
      const subCents = this.#subscriptionSellingPlanId ? Number(active.dataset.subPrice) : Number(active.dataset.price);
      const oneTimeCents = Number(active.dataset.price) || 0;

      atcLabel.textContent = this.#interpolate(this.dataset.completeLabel ?? 'Subscribe — [price]', {
        price: this.#formatMoney(subCents || 0),
      });

      if (oneTimeLinkLabel) {
        oneTimeLinkLabel.textContent = this.#interpolate(
          this.dataset.oneTimeLabel ?? 'One-time purchase — [price]',
          { price: this.#formatMoney(oneTimeCents) }
        );
      }
    } else if (total === 0) {
      atcLabel.textContent = this.#interpolate(this.dataset.incompleteLabel ?? 'Select at least [count] items', {
        count: targetSize,
      });
    } else {
      atcLabel.textContent = this.#interpolate(this.dataset.addMoreLabel ?? 'Add [remaining] more', {
        remaining: targetSize - total,
        count: targetSize,
      });
    }

    if (!complete && oneTimeLinkLabel) {
      oneTimeLinkLabel.textContent = this.#interpolate(
        this.dataset.oneTimeLabel ?? 'One-time purchase — [price]',
        { price: this.#formatMoney(Number(target?.dataset.price) || 0) }
      );
    }

    const reached = [...this.#markers].filter((marker) => Number(marker.dataset.size) <= total).pop();
    const reachedSize = Number(reached?.dataset.size) || 0;
    const exactSize = Number(active?.dataset.size) || 0;
    const priceTier = active || reached;
    const priceSize = Number(priceTier?.dataset.size) || 0;
    const boxCents = Number(priceTier?.dataset.subPrice || priceTier?.dataset.price) || 0;
    const unitCents = priceSize ? Math.floor(boxCents / priceSize) : 0;
    const offer = syncFreeBoxOffer(this, {
      size: reachedSize,
      exactSize,
      unitCents,
      formatMoney: (cents) => this.#formatMoney(cents),
      frequencyText: this.dataset.sellingPlanFrequency || '',
      subscribeSelected: this.#purchaseChoice !== 'one-time',
      flavors: [...this.#flavorMeta.entries()].map(([gid, meta]) => ({
        gid,
        variantId: gid.split('/').pop() || '',
        title: meta.productTitle,
        image: meta.imageSrc || '',
        available: !meta.outOfStock,
      })),
    });

    if (oneTimeButton) oneTimeButton.hidden = false;
    delete this.dataset.purchaseMode;

    const purchase = this.refs.purchaseOptions;
    const trial = this.refs.trialNote;
    const extraOnly = this.dataset.extraBoxOffer === 'true' && this.dataset.freeBoxOffer !== 'true';
    const freeBox = this.dataset.freeBoxOffer === 'true';
    const showPlans = Boolean(
      complete && active && (exactSize === 6 || exactSize === 8 || (exactSize === 4 && (extraOnly || freeBox)))
    );
    const trialPack = Boolean(extraOnly && complete && active && exactSize === 4);
    if (purchase instanceof HTMLElement) purchase.hidden = !showPlans;
    if (trial instanceof HTMLElement) trial.hidden = !trialPack;
    const extraHead = extraOnly && this.refs.flavorHead instanceof HTMLElement;
    if (this.refs.flavorHead instanceof HTMLElement) this.refs.flavorHead.hidden = extraHead ? false : !showPlans;
    if (this.refs.addedBadge instanceof HTMLElement) {
      const showAdded = extraHead ? total > 0 : showPlans;
      this.refs.addedBadge.hidden = !showAdded;
      this.refs.addedBadge.textContent = showAdded ? `${extraHead ? total : exactSize} Added` : '';
    }

    if (showPlans && active) {
      const oneTimeCents = Number(active.dataset.price) || 0;
      const subCents = Number(active.dataset.subPrice) || oneTimeCents;
      const save = Number(active.dataset.savePct) || 0;
      if (this.refs.subscribePrice) this.refs.subscribePrice.textContent = this.#formatMoney(subCents);
      if (this.refs.subscribeCompare) {
        this.refs.subscribeCompare.textContent = this.#formatMoney(oneTimeCents);
        this.refs.subscribeCompare.hidden = subCents >= oneTimeCents;
      }
      if (this.refs.oneTimePlanPrice) this.refs.oneTimePlanPrice.textContent = this.#formatMoney(oneTimeCents);
      if (this.refs.saveBadge) {
        this.refs.saveBadge.hidden = save <= 0;
        if (save > 0) this.refs.saveBadge.textContent = `SAVE ${save}%`;
      }
      if (this.refs.subscribeRadio instanceof HTMLInputElement) {
        this.refs.subscribeRadio.checked = !trialPack && this.#purchaseChoice !== 'one-time';
      }
      if (this.refs.oneTimeRadio instanceof HTMLInputElement) {
        this.refs.oneTimeRadio.checked = trialPack || this.#purchaseChoice === 'one-time';
      }
    }

    const subscribePlan = this.refs.subscribeRadio?.closest('.ctr-extra-purchase__plan');
    if (subscribePlan instanceof HTMLElement) subscribePlan.hidden = Boolean(extraOnly && trialPack);

    const wantsOneTime = trialPack || (showPlans && this.#purchaseChoice === 'one-time') || offer.purchaseMode === 'one-time';
    const hideOneTimeLink = purchase instanceof HTMLElement || (trialPack && this.dataset.extraBoxOffer === 'true');
    if (hideOneTimeLink && oneTimeButton) oneTimeButton.hidden = true;

    if (complete && active && wantsOneTime) {
      atcLabel.textContent = this.#interpolate(this.dataset.oneTimeLabel ?? 'One-time purchase — [price]', {
        price: this.#formatMoney(Number(active.dataset.price) || 0),
      });
      if (oneTimeButton) oneTimeButton.hidden = true;
      this.dataset.purchaseMode = 'one-time';
    }

    if (extraOnly && complete && active) {
      atcLabel.textContent = 'Checkout';
      const oneTimeCents = Number(active.dataset.price) || 0;
      const subCents = Number(active.dataset.subPrice) || oneTimeCents;
      const paying = trialPack || this.#purchaseChoice === 'one-time' ? oneTimeCents : subCents;
      syncExtraUnit(this, paying, exactSize, (cents) => this.#formatMoney(cents));
    } else {
      syncExtraUnit(this, 0, 0, (cents) => this.#formatMoney(cents));
    }

    this.#syncExtraBox(Boolean(extraOnly && complete && exactSize === 8));

    this.#markers.forEach((marker) => this.#updateTierPerUnit(marker));
    this.#watchTierPrices();
  }

  toggleExtraBox() {
    const menu = this.querySelector('[data-extra-box-menu]');
    const toggle = this.querySelector('[data-extra-box-toggle]');
    if (!(menu instanceof HTMLElement) || !(toggle instanceof HTMLElement)) return;
    const open = menu.hidden;
    menu.hidden = !open;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /**
   * @param {Event} event
   */
  selectExtraBox(event) {
    const option = event.target instanceof Element ? event.target.closest('[data-extra-option]') : null;
    if (!(option instanceof HTMLElement)) return;
    this.#extraTouched = true;
    this.#extraGid = option.dataset.extraOption || '';
    this.#paintExtraBox();
    const menu = this.querySelector('[data-extra-box-menu]');
    const toggle = this.querySelector('[data-extra-box-toggle]');
    if (menu instanceof HTMLElement) menu.hidden = true;
    if (toggle instanceof HTMLElement) toggle.setAttribute('aria-expanded', 'false');
  }

  /**
   * @param {boolean} show
   */
  #syncExtraBox(show) {
    const row = this.querySelector('[data-extra-box]');
    if (!(row instanceof HTMLElement)) return;
    row.hidden = !show;
    if (!show) return;

    const menu = row.querySelector('[data-extra-box-menu]');
    if (!(menu instanceof HTMLElement)) return;
    const signature = [...this.#flavorMeta.keys()].join('|');
    if (menu.dataset.signature !== signature) {
      menu.dataset.signature = signature;
      menu.replaceChildren();
      for (const [gid, meta] of this.#flavorMeta) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ctr-extra-box__option';
        button.dataset.extraOption = gid;
        button.setAttribute('role', 'option');
        const image = document.createElement('img');
        image.alt = '';
        image.width = 40;
        image.height = 40;
        if (meta.imageSrc) image.src = meta.imageSrc;
        else image.hidden = true;
        const label = document.createElement('span');
        label.textContent = meta.productTitle || '';
        button.append(image, label);
        button.addEventListener('click', (event) => this.selectExtraBox(event));
        menu.append(button);
      }
    }

    const kept = this.#extraTouched && (this.#selections.get(this.#extraGid) || 0) > 0;
    if (!kept) {
      this.#extraGid = majorityFlavor(this.#selections, this.#flavorMeta.keys());
      this.#extraTouched = false;
    }
    this.#paintExtraBox();
  }

  #paintExtraBox() {
    const row = this.querySelector('[data-extra-box]');
    if (!(row instanceof HTMLElement)) return;
    const meta = this.#flavorMeta.get(this.#extraGid);
    const name = row.querySelector('[data-extra-box-name]');
    const image = row.querySelector('[data-extra-box-image]');
    if (name) name.textContent = meta?.productTitle || 'Select';
    if (image instanceof HTMLImageElement) {
      if (meta?.imageSrc) {
        image.src = meta.imageSrc;
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }
    for (const option of row.querySelectorAll('[data-extra-option]')) {
      if (!(option instanceof HTMLElement)) continue;
      const selected = option.dataset.extraOption === this.#extraGid;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
    }
  }

  /**
   * Re-renders a tier marker's per-unit price after the Skio API reconciled its size.
   *
   * @param {HTMLElement} marker
   */
  #updateTierPerUnit(marker) {
    const perUnit = marker.querySelector('[data-tier-per-unit]');
    const size = Math.max(1, Number(marker.dataset.size) || 1);
    const oneTime = Number(marker.dataset.price) || 0;
    const sub = Number(marker.dataset.subPrice) || oneTime;
    const boxCents = this.dataset.freeBoxOffer === 'true' ? sub : oneTime;
    const eachCents =
      this.dataset.freeBoxOffer === 'true' ? perBoxCentsFromTotal(boxCents, size) : Math.round(boxCents / size);
    if (!perUnit) return;
    const next = `${this.#formatMoney(eachCents)}/ea`;
    if (perUnit.textContent !== next) perUnit.textContent = next;
  }

  /**
   * Intelligems rewrites marker prices to rounded subscribe amounts.
   * Halloween keeps the floored per-box price the cart charges.
   */
  #watchTierPrices() {
    if (this.dataset.freeBoxOffer !== 'true' || this.#tierPriceObserver) return;

    this.#tierPriceObserver = new MutationObserver(() => {
      for (const marker of this.#markers) this.#updateTierPerUnit(marker);
    });

    for (const marker of this.#markers) {
      const perUnit = marker.querySelector('[data-tier-per-unit]');
      if (perUnit) {
        this.#tierPriceObserver.observe(perUnit, { childList: true, characterData: true, subtree: true });
      }
    }
  }

  /**
   * Finds the variant gid for the flavor row an event originated from.
   *
   * @param {Event} event
   * @returns {string | undefined}
   */
  #rowGid(event) {
    if (!(event.target instanceof Element)) return undefined;
    const row = event.target.closest('[data-flavor-row]');
    return row instanceof HTMLElement ? row.dataset.variantGid : undefined;
  }

  /**
   * Replaces [token] placeholders in a label template.
   *
   * @param {string} template
   * @param {Record<string, string | number>} values
   * @returns {string}
   */
  #interpolate(template, values) {
    return template.replace(/\[(\w+)\]/g, (match, token) => (token in values ? String(values[token]) : match));
  }

  /**
   * Formats money in cents, replicating the `money` liquid filter.
   *
   * @param {number} cents - The money value in cents.
   * @returns {string}
   */
  #formatMoney(cents) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent?.trim() || '{{ amount }}';
    const currency = this.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      const roundedNumber = (cents / 100).toFixed(precision);
      let [whole, fraction] = roundedNumber.split('.');
      if (!whole) whole = '0';
      if (!fraction) fraction = '';

      whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

      return precision <= 0 ? whole : whole + decimalSeparator + fraction.padEnd(precision, '0');
    });
  }
}

if (!customElements.get('box-builder-component')) {
  customElements.define('box-builder-component', BoxBuilderComponent);
}
